import React, { useMemo } from 'react';
import type {
  ElementTransform,
  HtmlToReactOptions,
  NodeTransformContext,
} from './types';

/** React prop 명 매핑 테이블 */
const ATTRIBUTE_NAME_MAP: Record<string, string> = {
  for: 'htmlFor',
  tabindex: 'tabIndex',
  colspan: 'colSpan',
  rowspan: 'rowSpan',
  maxlength: 'maxLength',
  minlength: 'minLength',
  readonly: 'readOnly',
  contenteditable: 'contentEditable',
  spellcheck: 'spellCheck',
  autocomplete: 'autoComplete',
  autofocus: 'autoFocus',
  crossorigin: 'crossOrigin',
  enctype: 'encType',
  novalidate: 'noValidate',
  usemap: 'useMap',
  srcset: 'srcSet',
  datetime: 'dateTime',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  frameborder: 'frameBorder',
  allowfullscreen: 'allowFullScreen',
};

/** HTML 에서 `checked`/`selected` 는 값과 무관하게 존재하면 참 */
const BOOLEAN_TO_DEFAULT_MAP: Record<string, string> = {
  checked: 'defaultChecked',
  selected: 'defaultSelected',
};

/** 정적 값 속성 → React 비제어 prop. (controlled 경고 방지: onChange 없는 read-only 렌더) */
const VALUE_TO_DEFAULT_MAP: Record<string, string> = {
  value: 'defaultValue',
};

/** `style` 문자열 → camelCase 객체. 첫 `:` 기준 split 으로 url(http://) 등 값 보존. */
const parseStyleString = (value: string): Record<string, string> => {
  const styleObj: Record<string, string> = {};
  value.split(';').forEach((rule) => {
    const colonIndex = rule.indexOf(':');
    if (colonIndex === -1) return;
    const key = rule.slice(0, colonIndex).trim();
    const val = rule.slice(colonIndex + 1).trim();
    if (!key || !val) return;
    // CSS 속성명 camelCase 변환 (e.g. white-space → whiteSpace, -webkit-x → WebkitX)
    const camelKey = key
      .replace(/^-ms-/, 'ms-')
      .replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
    styleObj[camelKey] = val;
  });
  return styleObj;
};

/** 코어 기본 속성 매핑. transform 호출 전, `ctx.props` 의 베이스를 만든다. */
const buildProps = (
  element: Element,
  tagName: string,
  index: number,
): Record<string, unknown> => {
  const props: Record<string, unknown> = { key: `${tagName}-${index}` };

  Array.from(element.attributes).forEach((attr) => {
    const { name, value } = attr;
    const lower = name.toLowerCase();

    // class → className
    if (lower === 'class') {
      props.className = value;
      return;
    }

    // style → camelCase 객체 (첫 ':' split)
    if (lower === 'style') {
      props.style = parseStyleString(value);
      return;
    }

    // on* 인라인 핸들러 drop (React 가 문자열 핸들러 경고/무시 → 제거가 안전. 읽기 렌더라 의미 손실 없음)
    if (lower.startsWith('on')) {
      return;
    }

    // 정적 boolean checked/selected → defaultChecked/defaultSelected (존재 = true)
    if (BOOLEAN_TO_DEFAULT_MAP[lower]) {
      props[BOOLEAN_TO_DEFAULT_MAP[lower]] = true;
      return;
    }

    // 정적 value → defaultValue (controlled 경고 방지)
    if (VALUE_TO_DEFAULT_MAP[lower]) {
      props[VALUE_TO_DEFAULT_MAP[lower]] = value;
      return;
    }

    // 표준 HTML → React 속성명 매핑
    const mapped = ATTRIBUTE_NAME_MAP[lower];
    if (mapped) {
      props[mapped] = value;
      return;
    }

    // 그 외(data-*/aria-*/id/href/src/alt 등)는 그대로 복사
    props[name] = value;
  });

  return props;
};

/**
 * DOM 노드를 React 노드로 재귀 변환.
 * 텍스트 노드 → textContent. 요소 노드 → props 빌드 + 자식 재귀 + transform 호출.
 */
const convertNode = (
  node: Node,
  index: number,
  transform?: ElementTransform,
): React.ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const tagName = element.tagName.toLowerCase();

    const children = Array.from(element.childNodes).map((child, i) =>
      convertNode(child, i, transform),
    );

    const props = buildProps(element, tagName, index);

    if (transform) {
      const ctx: NodeTransformContext = {
        element,
        tagName,
        index,
        props,
        children,
      };
      const replaced = transform(ctx);
      // ReactNode(null 포함은 명시 반환만 대체로 취급하기 위해 undefined 만 기본 렌더로 통과).
      if (replaced !== undefined) {
        return replaced;
      }
      // transform 이 ctx.props/ctx.children 를 in-place 변형했을 수 있으므로 그것을 사용.
      return React.createElement(tagName, ctx.props, ...ctx.children);
    }

    return React.createElement(tagName, props, ...children);
  }

  return null;
};

/**
 * HTML 문자열 → React 노드 트리로 재구성 (DOMParser 파싱)
 *
 * dangerouslySetInnerHTML 과 달리 주입 요소가 React 트리의 일부 → 재렌더(콘텐츠 로드·번역)에도 살아남음.
 * 요소별 커스텀(멘션/첨부/zoom 등)은 options.transform, 파싱 전 raw 가공은 options.preprocess.
 * 결과는 useMemo 로 캐시. transform/preprocess 를 매 렌더 새 함수로 넘기면(인라인) 캐시가 안 먹어 매번 다시 파싱됨 → useCallback 으로 고정해서 넘길 것.
 */
const useHtmlToReact = (
  html: string | null,
  options?: HtmlToReactOptions,
): React.ReactNode => {
  const transform = options?.transform;
  const preprocess = options?.preprocess;

  return useMemo<React.ReactNode>(() => {
    if (!html) return null;

    const source = preprocess ? preprocess(html) : html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(source, 'text/html');

    return Array.from(doc.body.childNodes).map((node, i) =>
      convertNode(node, i, transform),
    );
  }, [html, transform, preprocess]);
};

export default useHtmlToReact;
